import React, { useState } from 'react';
import { Section, FadeIn, Button } from './ui';
import { ChevronDown, ShieldCheck, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const faqs = [{
  q: '¿Necesito experiencia previa?',
  a: 'No. El plan está diseñado para empezar desde cero, sin conocimientos previos. Cada paso está explicado con claridad para mujeres ocupadas.'
}, {
  q: '¿Cuánto tiempo requiere al día?',
  a: 'Entre 5 y 20 minutos diarios. Está diseñado para mujeres con vida ocupada — no necesitas reorganizar tu rutina, solo aplicar micro-ajustes específicos.'
}, {
  q: '¿Funciona si tengo otras condiciones de salud?',
  a: 'Este plan es informativo y complementario. Siempre consulta a tu médico ante condiciones específicas antes de hacer cambios importantes en tu estilo de vida.'
}, {
  q: '¿Cómo recibo el material?',
  a: 'Inmediatamente después de tu compra recibes acceso directo a la descarga del PDF — el plan completo más los 3 bonos exclusivos.'
}, {
  q: '¿Qué pasa si no funciona para mí?',
  a: 'Tienes 7 días de garantía total. Si no estás satisfecha, te devolvemos tu dinero completo. Sin preguntas, sin formularios interminables.'
}, {
  q: '¿Es seguro comprar aquí?',
  a: 'Sí. El proceso de pago es 100% seguro y encriptado a través de Hotmart, una de las plataformas más reconocidas del mundo para productos digitales.'
}];
const AccordionItem = ({
  q,
  a,
  defaultOpen = false
}: {
  q: string;
  a: string;
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return <div className="border-b border-cream-dark last:border-0" data-id="element-106">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none" data-id="element-107">
        <span className="text-lg font-bold text-brown pr-8" data-id="element-108">{q}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-109">
          <ChevronDown className="w-5 h-5 text-gold" data-id="element-110" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-111">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-112">
            <p className="pb-6 text-brown-light" data-id="element-113">{a}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export const FAQSection = () => {
  return <>
      <Section bg="bg-white" data-id="element-114">
        <FadeIn data-id="element-115">
          <div className="max-w-3xl mx-auto" data-id="element-116">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-117">
              Preguntas Frecuentes
            </h2>
            <div className="bg-cream-light rounded-3xl p-6 md:p-10 shadow-sm" data-id="element-118">
              {faqs.map((faq, i) => <AccordionItem key={i} q={faq.q} a={faq.a} defaultOpen={i === 0} data-id="element-119" />)}
            </div>
          </div>
        </FadeIn>
      </Section>

      <Section bg="bg-cream-dark/30" className="border-t border-cream-dark" data-id="element-120">
        <FadeIn data-id="element-121">
          <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden" data-id="element-122">
            <div className="grid md:grid-cols-2" data-id="element-123">
              <div className="p-8 md:p-10 lg:p-12 flex flex-col justify-center" data-id="element-124">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-125">
                  El cambio que tu cuerpo necesita, al alcance de un clic.
                </h2>
                <p className="text-base text-brown-light mb-6" data-id="element-126">
                  Acceso instantáneo en formato PDF al plan completo más los 3
                  bonos exclusivos.
                </p>

                {/* What's included */}
                <div className="mb-6 bg-cream-light rounded-2xl p-5 border border-cream-dark" data-id="element-127">
                  <p className="text-xs font-bold text-brown uppercase tracking-wider mb-3" data-id="element-128">
                    Incluye hoy:
                  </p>
                  <ul className="space-y-2 text-sm" data-id="element-129">
                    <li className="flex items-start gap-2" data-id="element-130">
                      <Check className="w-4 h-4 text-sage mt-0.5 shrink-0" data-id="element-131" />
                      <span className="text-brown" data-id="element-132">
                        <strong data-id="element-133">Plan de 21 Días</strong> — 5 capítulos + 3
                        herramientas
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-134">
                      <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" data-id="element-135" />
                      <span className="text-brown-light" data-id="element-136">
                        <strong className="text-brown" data-id="element-137">Bono #1:</strong> Reset
                        50+ — De Agotada a Encendida en 72h{' '}
                        <span className="text-sage font-bold" data-id="element-138">(GRATIS)</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-139">
                      <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" data-id="element-140" />
                      <span className="text-brown-light" data-id="element-141">
                        <strong className="text-brown" data-id="element-142">Bono #2:</strong> SOS
                        Hormonal — Menú Anticrisis 5 min{' '}
                        <span className="text-sage font-bold" data-id="element-143">(GRATIS)</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-144">
                      <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" data-id="element-145" />
                      <span className="text-brown-light" data-id="element-146">
                        <strong className="text-brown" data-id="element-147">Bono #3:</strong> Ritual
                        Matutino — 5 minutos que reactivan{' '}
                        <span className="text-sage font-bold" data-id="element-148">(GRATIS)</span>
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="mb-6" data-id="element-149">
                  <span className="text-xs text-brown-light uppercase tracking-wider font-bold" data-id="element-150">
                    Plan + 3 Bonos · Inversión única
                  </span>
                  <div className="flex items-baseline gap-3 mt-1" data-id="element-151">
                    <span className="text-2xl text-brown-light/50 line-through font-medium" data-id="element-152">
                      $97
                    </span>
                    <span className="text-5xl font-bold text-coral" data-id="element-153">$17</span>
                    <span className="text-2xl text-brown-light font-normal" data-id="element-154">
                      USD
                    </span>
                  </div>
                  <span className="inline-block mt-2 bg-sage-light text-sage text-sm font-bold px-3 py-1 rounded-full" data-id="element-155">
                    Ahorra más del 70%
                  </span>
                </div>

                <span id="precio" className="block scroll-mt-24" data-id="element-156"></span>
                <Button fullWidth href="https://pay.hotmart.com/W105376697A" onClick={() => {
                try {
                  const fbq = (window as any).fbq;
                  if (fbq) fbq('track', 'AddToCart', {
                    currency: 'USD',
                    value: 17,
                    content_name: 'Plan de 21 Días de Vitalidad Menopáusica',
                    content_type: 'product'
                  });
                } catch (e) {}
              }} data-id="element-157">
                  ¡SÍ, QUIERO RECUPERAR MI ENERGÍA AHORA!
                </Button>

                <div className="mt-6 bg-cream border border-cream-dark rounded-2xl p-5 text-center" data-id="element-158">
                  <ShieldCheck className="w-7 h-7 text-sage mx-auto mb-2" data-id="element-159" />
                  <h4 className="font-bold text-brown mb-1 text-base" data-id="element-160">
                    Garantía de 7 Días Sin Riesgo
                  </h4>
                  <p className="text-xs text-brown-light leading-relaxed" data-id="element-161">
                    Si en los próximos 7 días sientes que el Plan no fue para
                    ti, te devolvemos cada centavo. Sin preguntas.
                  </p>
                </div>

                <p className="mt-4 text-xs text-center text-brown-light/80" data-id="element-162">
                  Acceso instantáneo tras la compra. Pago 100% seguro.
                </p>
              </div>

              {/* Right column: product showcase with bonuses */}
              <div className="bg-gradient-to-br from-sage-light/30 to-cream p-6 md:p-8 lg:p-10 flex flex-col items-center justify-center gap-4 relative overflow-hidden" data-id="element-163">
                <div className="absolute inset-0 bg-gradient-to-br from-sage/10 to-transparent" data-id="element-164"></div>

                {/* Main plan image */}
                <div className="relative z-10 w-full" data-id="element-165">
                  <span className="block text-center text-xs font-bold text-gold uppercase tracking-wider mb-2" data-id="element-166">
                    Plan Principal
                  </span>
                  <img src="https://cdn.magicpatterns.com/uploads/qfE7aXZCJfEgvzUdLV7b1P/eBook.png" alt="Plan de 21 Días" className="w-full max-w-xs mx-auto rounded-xl shadow-2xl" data-id="element-167" />
                </div>

                {/* 3 Bonus thumbnails */}
                <div className="relative z-10 w-full mt-4" data-id="element-168">
                  <div className="flex items-center justify-center gap-2 mb-3" data-id="element-169">
                    <span className="h-px bg-brown-light/30 flex-1" data-id="element-170"></span>
                    <span className="text-xs font-bold text-coral uppercase tracking-wider" data-id="element-171">
                      + 3 Bonos Gratis Hoy
                    </span>
                    <span className="h-px bg-brown-light/30 flex-1" data-id="element-172"></span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 md:gap-3" data-id="element-173">
                    <div className="aspect-[16/9] bg-cream-light rounded-lg overflow-hidden shadow-md" data-id="element-174">
                      <img src="https://cdn.magicpatterns.com/uploads/3Gaq57BKCUtTjyUKZsJ5YX/Bono_1.png" alt="Bono 1: Reset 50+" className="w-full h-full object-cover" data-id="element-175" />
                    </div>
                    <div className="aspect-[16/9] bg-cream-light rounded-lg overflow-hidden shadow-md" data-id="element-176">
                      <img src="https://cdn.magicpatterns.com/uploads/xA4XtyjAKJK63Jo8zG4njz/Bono_2..png" alt="Bono 2: SOS Hormonal" className="w-full h-full object-cover" data-id="element-177" />
                    </div>
                    <div className="aspect-[16/9] bg-cream-light rounded-lg overflow-hidden shadow-md" data-id="element-178">
                      <img src="https://cdn.magicpatterns.com/uploads/4daWHgzJb95gMad7kD3S9D/Bono_3.png" alt="Bono 3: Ritual Matutino" className="w-full h-full object-cover" data-id="element-179" />
                    </div>
                  </div>
                  <p className="text-center text-xs text-brown-light/70 mt-3 italic" data-id="element-180">
                    Valor combinado:{' '}
                    <span className="line-through" data-id="element-181">$141 USD</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>
    </>;
};